import { Modal } from 'antd';
import userApi from 'api/userApi';
import { useAppDispatch, useAppSelector, useAppTranslation } from 'app/hooks';
import CloseIcon from 'assets/icons/CloseIcon';
import DepositIcon from 'assets/icons/DepositIcon';
import WarningIcon from 'assets/icons/WarningIcon';
import { BiMoneyWithdraw } from 'react-icons/bi';
import CurrencySwicher from 'components/Commons/CurrencySwitcher';
import { ShowModalProps } from 'components/Commons/Header';
import StarRating from 'components/Commons/StarRating';
import { showInfoModal } from 'components/Modals/CommonModals';
import { selectCashierMenuTab, setCashierMenuTab } from 'features/home/homeSlice';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import useThemes from 'hooks/useThemes';
import useUserDetail from 'hooks/useUserDetail';
import React, { ReactNode, useEffect, useState } from 'react';
import { BsBank } from 'react-icons/bs';
import { FaCoins, FaCubes, FaHistory, FaLink, FaWallet } from 'react-icons/fa';
import { FaCircleInfo, FaRightLeft } from 'react-icons/fa6';
import { RiP2PLine } from 'react-icons/ri';
import { platformStates } from 'redux/platformSlice';
import { selectCurrentUser } from 'redux/userSlice';
import Banking from '../Banking';
import CashierStatus from '../CashierStatus';
import CryptoContainer from '../CryptoContainer';
import PaymentMethods from '../PaymentMethods';
import SwapToken from '../SwapToken';
import TransactionHistory from '../TransactionHistory';
import UserWallets from '../UserWallets';
import WalletConnect from '../WalletConnect';
import AchDebitDepositModal from './AchDebitDepositModal';
import CryptoWalletDepositModal from './CryptoWalletDepositModal';
import CryptoWalletWithdrawModal from './CryptoWalletWithdrawModal';
import DepositModal from './DepositModal';
import FiatWithdrawModal from './FiatWithdrawModal';
import P2pTab from './P2pTab';
import Staking from './Staking';
import VinpayConnectModal from './VinpayConnectModal';
import VinpayDepositModal from './VinpayDepositModal';
import VinpayWithdrawModal from './VinpayWithdrawModal';
import WithdrawModal from './WithdrawModal';

interface CashierModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onClose: () => void;
}

interface CashierMenu {
  id: number;
  name: string;
  translation: string;
  image: string | ReactNode;
  acceptTester: boolean;
}

const menus: CashierMenu[] = [
  {
    id: 1,
    name: 'Status',
    translation: 'transactionHistory.header.status.title',
    image: '',
    acceptTester: true,
  },
  {
    id: 2,
    name: 'Wallet',
    translation: 'common.wallet',
    image: '',
    acceptTester: false,
  },
  {
    id: 11,
    name: 'Crypto',
    translation: 'cashier.crypto',
    image: '',
    acceptTester: false,
  },
  {
    id: 9,
    name: 'Stake',
    translation: 'tbet.stake.title',
    image: '',
    acceptTester: false,
  },
  {
    id: 3,
    name: 'Wallet connect',
    translation: 'common.walletConnect',
    image: '',
    acceptTester: false,
  },
  {
    id: 4,
    name: 'Deposit',
    translation: 'deposit.title',
    image: '',
    acceptTester: false,
  },
  {
    id: 5,
    name: 'Withdraw',
    translation: 'withdraw.title',
    image: '',
    acceptTester: false,
  },
  {
    id: 6,
    name: 'Swap',
    translation: 'tbet.buy.button',
    image: '',
    acceptTester: false,
  },
  {
    id: 7,
    name: 'Transaction',
    translation: 'transactionHistory.title',
    image: '',
    acceptTester: true,
  },
  {
    id: 8,
    name: 'Banking',
    translation: 'banking.title',
    image: '',
    acceptTester: false,
  },
  {
    id: 10,
    name: 'P2P',
    translation: 'P2P',
    image: '',
    acceptTester: false,
  },
];

const showModalInitialState = {
  depositModal: 0,
  isDeposit: true,
  paymentMethodsModal: false,
  achDebitDepositModal: false,
  withdrawModal: false,
  fiatWithdrawModal: false,
  promotionModal: false,
  support: false,
  transferModal: false,
  eventModal: false,
  headerMenu: false,
  userProfile: false,
  userBankbookModal: false,
  cryptoWalletDepositModal: false,
  cryptoWalletWithdrawModal: false,
  vinpayDepositModal: false,
  vinpayWithdrawModal: false,
  walletConnectModal: false,
  tbetBuySellModal: false,
  cashierModal: false,
  vinpayConnect: false,
};

const CashierModal: React.FunctionComponent<CashierModalProps> = ({
  isOpen,
  onCancel,
  onClose,
}) => {
  const t = useAppTranslation();
  const { currentTheme } = useThemes();

  const { isUserLoggedIn, getUser } = useUserDetail();
  const handleResponseError = useHandleResponseError();
  const { isPhone, isTablet } = useAppSelector(platformStates);

  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const cashierMenuTab = useAppSelector(selectCashierMenuTab);

  const [currentTab, setCurrentTab] = useState<number>(1);
  const [currentHistoryTab, setCurrentHitoryTab] = useState<number>(1);
  const [vinpayTypeModal, setVinpayTypeModal] = useState<'DEPOSIT' | 'WITHDRAW' | ''>('');
  const [showModal, setShowModal] = useState<ShowModalProps>(showModalInitialState);

  const handleSetShowModal = (key: keyof ShowModalProps, value: boolean | number) => {
    setShowModal((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleWithdrawClick = () => {
    handleSetShowModal('withdrawModal', !showModal.withdrawModal);
  };

  const handleFiatWithdrawClick = () => {
    handleSetShowModal('fiatWithdrawModal', !showModal.fiatWithdrawModal);
  };

  const handleAchDebitDepositClick = () => {
    handleSetShowModal('achDebitDepositModal', !showModal.achDebitDepositModal);
  };

  const handleCryptoWalletDepositClick = () => {
    handleSetShowModal('cryptoWalletDepositModal', !showModal.cryptoWalletDepositModal);
  };

  const handleCryptoWalletWithdrawClick = () => {
    handleSetShowModal('cryptoWalletWithdrawModal', !showModal.cryptoWalletWithdrawModal);
  };

  const handleConnectVinpay = async (showType: 'WITHDRAW' | 'DEPOSIT') => {
    const { ok, error } = await userApi.createVinpayAccount();
    Modal.destroyAll();
    if (ok) {
      getUser();
      if (showType === 'DEPOSIT') {
        handleSetShowModal('vinpayDepositModal', !showModal.vinpayDepositModal);
      } else {
        handleSetShowModal('vinpayWithdrawModal', !showModal.vinpayWithdrawModal);
      }
    }
    if (error) {
      handleResponseError(error);
    }
  };

  const openManualConnectVinpay = () => {
    handleSetShowModal('vinpayConnect', true);
  };

  const handleVinpayDepositClick = () => {
    if (currentUser?.isVinpayConnected) {
      handleSetShowModal('vinpayDepositModal', !showModal.vinpayDepositModal);
    } else {
      showInfoModal({
        title: t('vinpay.connectModal.title'),
        content: (
          <div>
            <p>{t('vinpay.connectModal.content')}</p>
            <div className="ant-modal-confirm-btns">
              <button className="ant-btn btn-submit" onClick={() => handleConnectVinpay('DEPOSIT')}>
                {t('vinpay.connectModal.createNow')}
              </button>
            </div>
          </div>
        ),
        width: 400,
        icon: <WarningIcon />,
        onOk: () => openManualConnectVinpay(),
        okText: t('vinpay.connectModal.manualy'),
        closeIcon: <CloseIcon width="20" height="20" isDarkMode={currentTheme === 'dark'} />,
        closable: true,
      });
      setVinpayTypeModal('DEPOSIT');
    }
  };

  const handleVinpayWithdrawClick = () => {
    if (currentUser?.isVinpayConnected) {
      handleSetShowModal('vinpayWithdrawModal', !showModal.vinpayWithdrawModal);
    } else {
      showInfoModal({
        title: t('vinpay.connectModal.title'),
        content: (
          <div>
            <p>{t('vinpay.connectModal.content')}</p>
            <div className="ant-modal-confirm-btns">
              <button
                className="ant-btn  btn-submit "
                onClick={() => handleConnectVinpay('WITHDRAW')}
              >
                {t('vinpay.connectModal.createNow')}
              </button>
            </div>
          </div>
        ),
        width: 400,
        icon: <WarningIcon />,
        onOk: () => openManualConnectVinpay(),
        okText: t('vinpay.connectModal.manualy'),
        closeIcon: <CloseIcon width="20" height="20" isDarkMode={currentTheme === 'dark'} />,
        closable: true,
      });

      setVinpayTypeModal('WITHDRAW');
    }
  };

  const handlePaymentMethodClick = (id: number) => {
    switch (id) {
      case 0:
        handleSetShowModal('depositModal', 2);
        break;
      case 3:
        handleVinpayDepositClick();
        break;
      case 4:
        handleAchDebitDepositClick();
        break;
      case 5:
        handleWithdrawClick();
        break;
      case 6:
        handleFiatWithdrawClick();
        break;
      case 7:
        handleCryptoWalletDepositClick();
        break;
      case 8:
        handleCryptoWalletWithdrawClick();
        break;
      case 9:
        handleVinpayWithdrawClick();
        break;
      case 10:

      default:
        showInfoModal({
          title: t('popup.comingSoon.title'),
          content: t('popup.comingSoon.content'),
        });
        break;
    }
  };

  useEffect(() => {
    menus[0].image = <FaCircleInfo className="w-5 h-5 cashier-menu-image" />;
    menus[1].image = <FaWallet className="w-5 h-5 cashier-menu-image" />;
    menus[2].image = <FaCoins className="w-5 h-5 cashier-menu-image" />;
    menus[3].image = <BsBank className="w-5 h-5 cashier-menu-image" />;
    menus[4].image = <FaLink className="w-5 h-5 cashier-menu-image" />;
    menus[5].image = <DepositIcon className="cashier-menu-image" width="20" height="20" />;
    menus[6].image = <BiMoneyWithdraw className="w-5 h-5 cashier-menu-image" />;
    // menus[5].image = <SwapIcon
    //   fill={currentTheme === 'dark' ? '#FFFFFF' : '#000000'}
    //   width="20px"
    //   height="20px"
    // />;
    menus[7].image = <FaRightLeft className="w-5 h-5 cashier-menu-image" />;
    menus[8].image = <FaHistory className="w-5 h-5 cashier-menu-image" />;
    menus[9].image = <FaCubes className="w-5 h-5  cashier-menu-image" />;
    menus[10].image = <RiP2PLine className="w-5 h-5 cashier-menu-image" />;
    // menus[6].image = <img
    //   src={currentTheme === 'dark' ? transaction_history : transaction_history_dark}
    //   width={20}
    //   height={20}
    //   alt="transaction history icon"
    // />
  }, []);

  useEffect(() => {
    return () => {
      onClose();
      setShowModal(showModalInitialState);
    };
  }, [isUserLoggedIn]);

  useEffect(() => {
    return () => {
      setCurrentTab(1);
    };
  }, [isOpen]);

  const handleShowVinpayType = () => {
    handleSetShowModal('vinpayConnect', false);
    if (vinpayTypeModal === 'DEPOSIT') {
      return handleSetShowModal('vinpayDepositModal', !showModal.vinpayDepositModal);
    }
    if (vinpayTypeModal === 'WITHDRAW') {
      handleSetShowModal('vinpayWithdrawModal', !showModal.vinpayWithdrawModal);
    }
  };

  useEffect(() => {
    if (!!cashierMenuTab) {
      setCurrentTab(cashierMenuTab);
      dispatch(setCashierMenuTab(null));
    }
  }, [isOpen]);

  return (
    <Modal
      className="common-modal cashier-modal"
      // title={t('footerMenu.cashier.title')}
      title={
        <div className="cashier-modal-title">
          <span>{t('footerMenu.cashier.title')}</span>

          <div className="cashier-modal-title-info">
            {isUserLoggedIn && <CurrencySwicher isReverse={true} />}{' '}
            {isUserLoggedIn && (
              <StarRating rating={currentUser?.vipLevel ? currentUser.vipLevel.vipLevel : 0} />
            )}
          </div>
        </div>
      }
      footer={null}
      visible={isOpen}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      onCancel={onCancel}
      width="800"
    >
      <div className="cashier-menu-body primary-text-color">
        <div className="cashier-menu">
          {menus
            .filter((menu) => {
              if (currentUser?.isTester) {
                return menu.acceptTester;
              }
              return true;
            })
            .map((menu) => (
              <div
                key={menu.id}
                onClick={() => setCurrentTab(menu.id)}
                className={`cashier-menu__item ${currentTab === menu.id && 'active'}`}
              >
                {(isTablet || isPhone) && menu.image}
                {!isTablet && !isPhone ? (
                  <span className="font-bold">{t(menu.translation)}</span>
                ) : (
                  <span className="font-bold">{t(menu.translation)}</span>
                )}
              </div>
            ))}
        </div>
        <div className="cashier-body">
          {currentTab === 1 && <CashierStatus />}
          {currentTab === 2 && <UserWallets />}
          {currentTab === 3 && <WalletConnect />}
          {currentTab === 4 && (
            <PaymentMethods handlePaymentMethodClick={handlePaymentMethodClick} type="deposit" />
          )}
          {currentTab === 5 && (
            <PaymentMethods
              handlePaymentMethodClick={handlePaymentMethodClick}
              type="withdraw"
              redirectToSwap={() => setCurrentTab(6)}
            />
          )}
          {currentTab === 6 && <SwapToken />}
          {currentTab === 7 && <TransactionHistory tab={currentHistoryTab} />}
          {currentTab === 8 && <Banking />}
          {currentTab === 9 && (
            <Staking
              redirectToHistory={() => {
                setCurrentTab(7);
                setCurrentHitoryTab(6);
              }}
            />
          )}
          {currentTab === 10 && <P2pTab isOpen={currentTab === 10} />}
          {/* {currentTab === 10 && (

          )} */}
          {currentTab === 11 && <CryptoContainer />}
        </div>
      </div>
      {isOpen && (
        <>
          <DepositModal
            title={t('deposit.title')}
            visible={showModal.depositModal === 2}
            closeModal={() => handleSetShowModal('depositModal', 0)}
            className="common-modal deposit-modal"
          />
          <AchDebitDepositModal
            title={t('deposit.title')}
            visible={showModal.achDebitDepositModal}
            closeModal={handleAchDebitDepositClick}
            className="common-modal withdraw-modal"
          />
          <CryptoWalletDepositModal
            title={t('deposit.title')}
            visible={showModal.cryptoWalletDepositModal}
            closeModal={handleCryptoWalletDepositClick}
            className="common-modal withdraw-modal"
          />
          <VinpayDepositModal
            title={t('deposit.title')}
            visible={showModal.vinpayDepositModal}
            closeModal={handleVinpayDepositClick}
            className="common-modal withdraw-modal"
          />
          <FiatWithdrawModal
            title={t('withdraw.title')}
            visible={showModal.fiatWithdrawModal}
            closeModal={handleFiatWithdrawClick}
            className="common-modal withdraw-modal"
          />
          <WithdrawModal
            title={t('withdraw.title')}
            visible={showModal.withdrawModal}
            closeModal={handleWithdrawClick}
            className="common-modal withdraw-modal"
          />
          <CryptoWalletWithdrawModal
            title={t('withdraw.title')}
            visible={showModal.cryptoWalletWithdrawModal}
            closeModal={handleCryptoWalletWithdrawClick}
            className="common-modal withdraw-modal"
          />
          <VinpayWithdrawModal
            title={t('withdraw.title')}
            visible={showModal.vinpayWithdrawModal}
            closeModal={handleVinpayWithdrawClick}
            className="common-modal withdraw-modal"
          />
          <VinpayConnectModal
            visible={showModal.vinpayConnect}
            closeModal={() => handleSetShowModal('vinpayConnect', false)}
            className="common-modal withdraw-modal"
            hiddenAutoCreate={true}
            onSuccess={handleShowVinpayType}
          />
        </>
      )}
    </Modal>
  );
};

export default CashierModal;
